import { Order, Stats, ApiResponse } from '../types';
import { API_BASE_URL } from '../utils/apiConfig';

export class ApiService {
    private static async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
        try {
            const token = localStorage.getItem('authToken');
            const fullUrl = `${API_BASE_URL}${endpoint}`;
            console.log(`🌐 [API] Making request to: ${fullUrl}`);
            console.log(`🌐 [API] Origin: ${window.location.origin}`);
            const response = await fetch(fullUrl, {
                credentials: 'include', // Important for cross-origin cookies
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0',
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                    ...options.headers,
                },
                ...options,
            });

            // Handle 304 Not Modified as a valid response
            if (response.status === 304) {
                console.log(`🔄 [API] 304 Not Modified for ${endpoint} - using cached data`);
                // For 304 responses, the server typically doesn't send a response body
                // We should treat this as a successful response but with empty data
                // The frontend should handle this gracefully
                return {
                    success: true,
                    data: {} as any
                };
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`❌ [API] HTTP error ${response.status} for ${fullUrl}:`, errorText);
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            const data = await response.json();
            console.log('🔍 [API] Response data:', data);

            // Return the backend response directly - it already has the correct structure
            return data;
        } catch (error) {
            console.error('API request failed:', error);

            // Check if it's a CORS error
            if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
                console.error(`🚫 [API] CORS Error detected for ${endpoint}`);
                console.error(`🚫 [API] This usually means the backend server is not running or CORS is not configured properly`);
                console.error(`🚫 [API] Frontend Origin: ${window.location.origin}`);
                console.error(`🚫 [API] API Base URL: ${API_BASE_URL}`);
            }

            throw error;
        }
    }

    static async getOrders(): Promise<ApiResponse<{ orders: Order[]; stats: Stats }>> {
        return this.request<{ orders: Order[]; stats: Stats }>('/api/orders');
    }

    static async getOrdersByStatus(status: number): Promise<ApiResponse<{ orders: Order[]; status: number }>> {
        return this.request<{ orders: Order[]; status: number }>(`/api/orders/status/${status}`);
    }

    static async getStats(): Promise<ApiResponse<Stats>> {
        return this.request<Stats>('/api/stats');
    }

    static async startMonitoring(): Promise<ApiResponse<{ message: string }>> {
        return this.request<{ message: string }>('/api/monitor/start', { method: 'POST' });
    }

    static async stopMonitoring(): Promise<ApiResponse<{ message: string }>> {
        return this.request<{ message: string }>('/api/monitor/stop', { method: 'POST' });
    }

    static async getMongoDBStatus(): Promise<ApiResponse<{ connected: boolean; totalOrders: number }>> {
        return this.request<{ connected: boolean; totalOrders: number }>('/api/mongodb/status');
    }

    // Enhanced API methods
    static async getEnhancedOrders(): Promise<ApiResponse<{ orders: Order[]; stats: Stats; shippingStats: any }>> {
        return this.request<{ orders: Order[]; stats: Stats; shippingStats: any }>('/api/enhanced-orders');
    }

    // Paginated Orders API
    static async getPaginatedOrders(params: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        status?: number | number[];
        country?: string | string[];
        search?: string;
        dateFrom?: string;
        dateTo?: string;
        shippingStatus?: string | string[];
        hasTracking?: boolean;
        period?: string;
    } = {}): Promise<ApiResponse<any>> {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                if (Array.isArray(value)) {
                    value.forEach(item => queryParams.append(key, item.toString()));
                } else {
                    queryParams.append(key, value.toString());
                }
            }
        });

        const queryString = queryParams.toString();
        const endpoint = `/api/enhanced-orders/paginated${queryString ? `?${queryString}` : ''}`;
        console.log('🔍 [API] getPaginatedOrders endpoint:', endpoint);

        return this.request(endpoint);
    }

    // Export orders (fetches ALL matching orders, no pagination)
    static async exportOrders(params: {
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        status?: string;
        country?: string;
        search?: string;
        period?: string;
        customStartDate?: string;
        customEndDate?: string;
    } = {}): Promise<ApiResponse<any>> {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value.toString());
            }
        });

        const queryString = queryParams.toString();
        const endpoint = `/api/enhanced-orders/export${queryString ? `?${queryString}` : ''}`;
        console.log('📥 [API] exportOrders endpoint:', endpoint);

        return this.request(endpoint);
    }

    static async getOrdersByCountry(country: string): Promise<ApiResponse<{ orders: Order[]; country: string }>> {
        return this.request<{ orders: Order[]; country: string }>(`/api/enhanced-orders/country/${country}`);
    }

    static async getMacedonianOrders(): Promise<ApiResponse<{ orders: Order[]; country: string }>> {
        return this.request<{ orders: Order[]; country: string }>('/api/enhanced-orders/macedonia');
    }

    static async getPackagingOrders(): Promise<ApiResponse<{ orders: Order[]; status: number }>> {
        return this.request<{ orders: Order[]; status: number }>('/api/enhanced-orders/packaging');
    }

    static async getSendingOrders(): Promise<ApiResponse<{ orders: Order[]; status: number }>> {
        return this.request<{ orders: Order[]; status: number }>('/api/enhanced-orders/sending');
    }

    static async getTransferOrders(): Promise<ApiResponse<{ orders: Order[]; status: number }>> {
        return this.request<{ orders: Order[]; status: number }>('/api/enhanced-orders/transfer');
    }

    static async getCompletedOrders(): Promise<ApiResponse<{ orders: Order[]; status: number }>> {
        return this.request<{ orders: Order[]; status: number }>('/api/enhanced-orders/completed');
    }

    static async getShippingStats(): Promise<ApiResponse<{ stats: any }>> {
        return this.request<{ stats: any }>('/api/enhanced-orders/shipping/stats');
    }

    static async getSupportedCountries(): Promise<ApiResponse<{ countries: string[] }>> {
        return this.request<{ countries: string[] }>('/api/enhanced-orders/shipping/countries');
    }

    static async getShipments(): Promise<ApiResponse<{ shipments: any[] }>> {
        return this.request<{ shipments: any[] }>('/api/enhanced-orders/shipping/shipments');
    }

    static async getShipmentsByStatus(status: string): Promise<ApiResponse<{ shipments: any[]; status: string }>> {
        return this.request<{ shipments: any[]; status: string }>(`/api/enhanced-orders/shipping/shipments/status/${status}`);
    }

    static async getShipmentByOrderId(orderId: number): Promise<ApiResponse<{ shipment: any }>> {
        return this.request<{ shipment: any }>(`/api/enhanced-orders/shipping/shipments/order/${orderId}`);
    }

    static async getComprehensiveAnalytics(): Promise<ApiResponse<{ orders: any; shipping: any; countries: any }>> {
        return this.request<{ orders: any; shipping: any; countries: any }>('/api/enhanced-orders/analytics/comprehensive');
    }

    // WooCommerce API methods
    static async getWooCommerceStores(): Promise<{ success: boolean; stores: any[] }> {
        return this.request('/api/woocommerce/stores') as unknown as Promise<{ success: boolean; stores: any[] }>;
    }

    static async getWooCommerceOrders(): Promise<{ success: boolean; orders: any[] }> {
        return this.request('/api/woocommerce/orders') as unknown as Promise<{ success: boolean; orders: any[] }>;
    }

    static async getWooCommerceSmartStatus(): Promise<{ success: boolean; status: any }> {
        return this.request('/api/woocommerce/smart/status') as unknown as Promise<{ success: boolean; status: any }>;
    }

    static async syncWooCommerceStore(storeId: string, forceFullSync: boolean = false): Promise<{ success: boolean; message?: string }> {
        return this.request(`/api/woocommerce/smart/sync/${storeId}`, {
            method: 'POST',
            body: JSON.stringify({ forceFullSync })
        }) as unknown as Promise<{ success: boolean; message?: string }>;
    }

    static async syncAllWooCommerceStores(forceFullSync: boolean = false): Promise<ApiResponse<any>> {
        return this.request<any>('/api/woocommerce/smart/sync-all', {
            method: 'POST',
            body: JSON.stringify({ forceFullSync })
        });
    }

    static async addWooCommerceStore(storeData: any): Promise<ApiResponse<any>> {
        return this.request<any>('/api/woocommerce/stores', {
            method: 'POST',
            body: JSON.stringify(storeData)
        });
    }

    static async updateWooCommerceStore(storeId: string, storeData: any): Promise<ApiResponse<any>> {
        return this.request<any>(`/api/woocommerce/smart/stores/${storeId}`, {
            method: 'PUT',
            body: JSON.stringify(storeData)
        });
    }

    static async deleteWooCommerceStore(storeId: string): Promise<{ success: boolean; message?: string }> {
        return this.request(`/api/woocommerce/smart/stores/${storeId}`, {
            method: 'DELETE'
        }) as unknown as Promise<{ success: boolean; message?: string }>;
    }

    static async getWooCommerceStoreStatus(storeId: string): Promise<ApiResponse<any>> {
        return this.request<any>(`/api/woocommerce/smart/stores/${storeId}/status`);
    }

    static async getWooCommerceSchedulerStatus(): Promise<ApiResponse<any>> {
        return this.request<any>('/api/woocommerce/smart/scheduler/status');
    }

    static async controlWooCommerceScheduler(action: 'start' | 'stop'): Promise<ApiResponse<any>> {
        return this.request<any>(`/api/woocommerce/smart/scheduler/${action}`, {
            method: 'POST'
        });
    }

    // AlterCPA Data API methods
    // AlterCPA removed for Serbia-CC
    static async getAlterCPAAccounts(): Promise<{ success: boolean; accounts: any[] }> {
        // Return empty array - AlterCPA removed
        return { success: true, accounts: [] };
        return this.request('/api/altercpa-data/accounts') as unknown as Promise<{ success: boolean; accounts: any[] }>;
    }

    static async createAlterCPAAccount(accountData: any): Promise<ApiResponse<{ account: any }>> {
        return this.request<{ account: any }>('/api/altercpa-data/accounts', {
            method: 'POST',
            body: JSON.stringify(accountData)
        });
    }

    static async updateAlterCPAAccount(accountId: number, accountData: any): Promise<ApiResponse<{ message: string }>> {
        return this.request<{ message: string }>(`/api/altercpa-data/accounts/${accountId}`, {
            method: 'PUT',
            body: JSON.stringify(accountData)
        });
    }

    static async deleteAlterCPAAccount(accountId: number): Promise<ApiResponse<{ message: string }>> {
        return this.request<{ message: string }>(`/api/altercpa-data/accounts/${accountId}`, { method: 'DELETE' });
    }

    static async getAlterCPATeams(): Promise<{ success: boolean; teams: any[] }> {
        return this.request('/api/altercpa-data/teams') as unknown as Promise<{ success: boolean; teams: any[] }>;
    }

    static async createAlterCPATeam(teamData: any): Promise<ApiResponse<{ team: any }>> {
        return this.request<{ team: any }>('/api/altercpa-data/teams', {
            method: 'POST',
            body: JSON.stringify(teamData)
        });
    }

    static async updateAlterCPATeam(teamId: number, teamData: any): Promise<ApiResponse<{ message: string }>> {
        return this.request<{ message: string }>(`/api/altercpa-data/teams/${teamId}`, {
            method: 'PUT',
            body: JSON.stringify(teamData)
        });
    }

    static async deleteAlterCPATeam(teamId: number): Promise<ApiResponse<{ message: string }>> {
        return this.request<{ message: string }>(`/api/altercpa-data/teams/${teamId}`, { method: 'DELETE' });
    }

    static async getAlterCPASources(): Promise<{ success: boolean; sources: any[] }> {
        return this.request('/api/altercpa-data/sources') as unknown as Promise<{ success: boolean; sources: any[] }>;
    }

    static async createAlterCPASource(sourceData: any): Promise<ApiResponse<{ source: any }>> {
        return this.request<{ source: any }>('/api/altercpa-data/sources', {
            method: 'POST',
            body: JSON.stringify(sourceData)
        });
    }

    static async updateAlterCPASource(sourceId: number, sourceData: any): Promise<ApiResponse<{ message: string }>> {
        return this.request<{ message: string }>(`/api/altercpa-data/sources/${sourceId}`, {
            method: 'PUT',
            body: JSON.stringify(sourceData)
        });
    }

    static async deleteAlterCPASource(sourceId: number): Promise<ApiResponse<{ message: string }>> {
        return this.request<{ message: string }>(`/api/altercpa-data/sources/${sourceId}`, { method: 'DELETE' });
    }

    static async getAlterCPAOffers(): Promise<{ success: boolean; offers: any[] }> {
        return this.request('/api/altercpa-data/offers') as unknown as Promise<{ success: boolean; offers: any[] }>;
    }

    static async createAlterCPAOffer(offerData: any): Promise<ApiResponse<{ offer: any }>> {
        return this.request<{ offer: any }>('/api/altercpa-data/offers', {
            method: 'POST',
            body: JSON.stringify(offerData)
        });
    }

    static async updateAlterCPAOffer(offerId: number, offerData: any): Promise<ApiResponse<{ message: string }>> {
        return this.request<{ message: string }>(`/api/altercpa-data/offers/${offerId}`, {
            method: 'PUT',
            body: JSON.stringify(offerData)
        });
    }

    static async deleteAlterCPAOffer(offerId: number): Promise<ApiResponse<{ message: string }>> {
        return this.request<{ message: string }>(`/api/altercpa-data/offers/${offerId}`, { method: 'DELETE' });
    }

    static async getAlterCPAStats(): Promise<{ success: boolean; stats: any }> {
        return this.request('/api/altercpa-data/stats') as unknown as Promise<{ success: boolean; stats: any }>;
    }

    static async bulkUpsertAlterCPAAccounts(accounts: any[]): Promise<{ success: boolean; created: number; updated: number; failed: number; message: string; errors?: any[] }> {
        return this.request('/api/altercpa-data/accounts/bulk-upsert', {
            method: 'POST',
            body: JSON.stringify({ accounts })
        }) as unknown as Promise<{ success: boolean; created: number; updated: number; failed: number; message: string; errors?: any[] }>;
    }

    // User Management API methods
    static async getUsers(): Promise<{ success: boolean; data: { users: any[]; count: number } }> {
        return this.request('/api/auth/users') as unknown as Promise<{ success: boolean; data: { users: any[]; count: number } }>;
    }

    static async createUser(userData: any): Promise<ApiResponse<{ user: any }>> {
        return this.request<{ user: any }>('/api/auth/users', {
            method: 'POST',
            body: JSON.stringify(userData)
        });
    }

    static async updateUser(userId: string, userData: any): Promise<ApiResponse<{ user: any }>> {
        return this.request<{ user: any }>(`/api/auth/users/${userId}`, {
            method: 'PUT',
            body: JSON.stringify(userData)
        });
    }

    static async deleteUser(userId: string): Promise<ApiResponse<{ message: string }>> {
        return this.request<{ message: string }>(`/api/auth/users/${userId}`, {
            method: 'DELETE'
        });
    }

    static async toggleUserStatus(userId: string): Promise<ApiResponse<{ user: any }>> {
        const user = await this.request<any>(`/api/auth/users/${userId}`, { method: 'GET' });
        return this.updateUser(userId, { isActive: !user.data.isActive });
    }

    // AlterCPA removed for Serbia-CC
    static async getUnlinkedAccounts(): Promise<{ success: boolean; data: { linkedAccountIds: number[] } }> {
        // Return empty array - AlterCPA removed
        return { success: true, data: { linkedAccountIds: [] } };
    }

    static async getAvailableRoles(): Promise<{ success: boolean; data: { roles: string[]; userRole: string } }> {
        return this.request('/api/auth/roles') as unknown as Promise<{ success: boolean; data: { roles: string[]; userRole: string } }>;
    }

    static async getAvailableCountries(): Promise<{ success: boolean; data: { countries: string[]; userCountries: string[] } }> {
        return this.request('/api/auth/countries') as unknown as Promise<{ success: boolean; data: { countries: string[]; userCountries: string[] } }>;
    }

    // ==================== GUARANTEES ====================
    // Guarantees removed for Serbia-CC - methods kept for reference but routes are disabled
    // All guarantee API calls will fail as routes are not mounted

    // Publishers
    static async getGuaranteesPublishers(queryString?: string): Promise<ApiResponse<{ publishers: any[] }>> {
        const timestamp = `_t=${Date.now()}`;
        const baseUrl = '/api/guarantees/publishers';
        const url = queryString ? `${baseUrl}?${queryString}&${timestamp}` : `${baseUrl}?${timestamp}`;
        return this.request<{ publishers: any[] }>(url);
    }

    static async createGuaranteesPublisher(publisherData: any): Promise<ApiResponse<any>> {
        return this.request('/api/guarantees/publishers', {
            method: 'POST',
            body: JSON.stringify(publisherData)
        });
    }

    static async updateGuaranteesPublisher(publisherId: string, publisherData: any): Promise<ApiResponse<any>> {
        return this.request(`/api/guarantees/publishers/${publisherId}`, {
            method: 'PUT',
            body: JSON.stringify(publisherData)
        });
    }

    static async deleteGuaranteesPublisher(publisherId: string): Promise<ApiResponse<any>> {
        return this.request(`/api/guarantees/publishers/${publisherId}`, { method: 'DELETE' });
    }

    // Guarantees
    static async getGuaranteesGuarantees(queryString?: string): Promise<ApiResponse<{ guarantees: any[] }>> {
        const timestamp = `_t=${Date.now()}`;
        const baseUrl = '/api/guarantees/guarantees';
        const url = queryString ? `${baseUrl}?${queryString}&${timestamp}` : `${baseUrl}?${timestamp}`;
        return this.request<{ guarantees: any[] }>(url);
    }

    static async createGuaranteesGuarantee(guaranteeData: any): Promise<ApiResponse<any>> {
        return this.request('/api/guarantees/guarantees', {
            method: 'POST',
            body: JSON.stringify(guaranteeData)
        });
    }

    static async updateGuaranteesGuarantee(guaranteeId: string, guaranteeData: any): Promise<ApiResponse<any>> {
        return this.request(`/api/guarantees/guarantees/${guaranteeId}`, {
            method: 'PUT',
            body: JSON.stringify(guaranteeData)
        });
    }

    static async deleteGuaranteesGuarantee(guaranteeId: string): Promise<ApiResponse<any>> {
        return this.request(`/api/guarantees/guarantees/${guaranteeId}`, { method: 'DELETE' });
    }

    // Payouts
    static async getGuaranteesPayouts(queryString?: string): Promise<ApiResponse<{ payouts: any[] }>> {
        const url = queryString ? `/api/guarantees/payouts?${queryString}` : '/api/guarantees/payouts';
        return this.request<{ payouts: any[] }>(url);
    }

    static async createGuaranteesPayout(payoutData: any): Promise<ApiResponse<any>> {
        return this.request('/api/guarantees/payouts', {
            method: 'POST',
            body: JSON.stringify(payoutData)
        });
    }

    static async updateGuaranteesPayout(payoutId: string, payoutData: any): Promise<ApiResponse<any>> {
        return this.request(`/api/guarantees/payouts/${payoutId}`, {
            method: 'PUT',
            body: JSON.stringify(payoutData)
        });
    }

    static async deleteGuaranteesPayout(payoutId: string): Promise<ApiResponse<any>> {
        return this.request(`/api/guarantees/payouts/${payoutId}`, { method: 'DELETE' });
    }

    // New Payout System
    static async getDefaultPayouts(): Promise<ApiResponse<{ defaultPayouts: any[] }>> {
        return this.request<{ defaultPayouts: any[] }>('/api/guarantees/payouts/default');
    }

    static async bulkImportDefaultPayouts(defaultPayouts: any[]): Promise<ApiResponse<any>> {
        return this.request('/api/guarantees/payouts/default/bulk-import', {
            method: 'POST',
            body: JSON.stringify({ defaultPayouts })
        });
    }

    static async updateDefaultPayoutLinks(payoutId: string, linkedProduct: string, linkedOfferId: string): Promise<ApiResponse<any>> {
        return this.request(`/api/guarantees/payouts/default/${payoutId}`, {
            method: 'PUT',
            body: JSON.stringify({ linkedProduct, linkedOfferId })
        });
    }

    static async deleteDefaultPayout(payoutId: string): Promise<ApiResponse<any>> {
        return this.request(`/api/guarantees/payouts/default/${payoutId}`, { method: 'DELETE' });
    }

    static async getCustomPayouts(): Promise<ApiResponse<{ customPayouts: any[] }>> {
        return this.request<{ customPayouts: any[] }>('/api/guarantees/payouts/custom');
    }

    static async updateCustomPayoutLinks(payoutId: string, linkedProduct: string, linkedOfferId: string): Promise<ApiResponse<any>> {
        return this.request(`/api/guarantees/payouts/custom/${payoutId}`, {
            method: 'PUT',
            body: JSON.stringify({ linkedProduct, linkedOfferId })
        });
    }

    static async deleteCustomPayout(payoutId: string): Promise<ApiResponse<any>> {
        return this.request(`/api/guarantees/payouts/custom/${payoutId}`, { method: 'DELETE' });
    }

    static async bulkImportCustomPayouts(customPayouts: any[]): Promise<ApiResponse<any>> {
        return this.request('/api/guarantees/payouts/custom/bulk-import', {
            method: 'POST',
            body: JSON.stringify({ customPayouts })
        });
    }

    static async getEffectivePayouts(): Promise<ApiResponse<{ effectivePayouts: any[] }>> {
        return this.request<{ effectivePayouts: any[] }>('/api/guarantees/payouts/effective');
    }

    // Stats and Performance
    static async getGuaranteesStats(queryString?: string): Promise<ApiResponse<any>> {
        const url = queryString ? `/api/guarantees/stats?${queryString}` : '/api/guarantees/stats';
        return this.request(url);
    }

    static async getGuaranteesPerformance(queryString?: string): Promise<ApiResponse<{ performance: any[] }>> {
        const url = queryString ? `/api/guarantees/performance?${queryString}` : '/api/guarantees/performance';
        return this.request<{ performance: any[] }>(url);
    }

    static async getGuaranteesStatsByCountry(queryString?: string): Promise<ApiResponse<{ statsByCountry: any[] }>> {
        const url = queryString ? `/api/guarantees/stats-by-country?${queryString}` : '/api/guarantees/stats-by-country';
        return this.request<{ statsByCountry: any[] }>(url);
    }

    static async getGuaranteesStatsBySource(queryString?: string): Promise<ApiResponse<{ statsBySource: any[] }>> {
        const url = queryString ? `/api/guarantees/stats-by-source?${queryString}` : '/api/guarantees/stats-by-source';
        return this.request<{ statsBySource: any[] }>(url);
    }

    static async getGuaranteesStatsByPublisher(queryString?: string): Promise<ApiResponse<{ statsByPublisher: any[] }>> {
        const url = queryString ? `/api/guarantees/stats-by-publisher?${queryString}` : '/api/guarantees/stats-by-publisher';
        return this.request<{ statsByPublisher: any[] }>(url);
    }

    static async getGuaranteesDebug(): Promise<ApiResponse<any>> {
        return this.request('/api/guarantees/debug');
    }

    static async getGuaranteesCalculatedPayouts(queryString?: string): Promise<ApiResponse<any>> {
        const url = queryString ? `/api/guarantees/calculated-payouts?${queryString}` : '/api/guarantees/calculated-payouts';
        return this.request(url);
    }

    static async getGuaranteesPublishersEnhanced(): Promise<ApiResponse<any>> {
        const timestamp = `_t=${Date.now()}`;
        return this.request(`/api/guarantees/publishers-enhanced?_t=${timestamp}`);
    }
    // End of Guarantees section - removed for Serbia-CC

    // ==================== PRODUCTS ====================

    static async getProducts(params?: {
        page?: number;
        limit?: number;
        search?: string;
        category?: string;
        stockStatus?: string;
        manufacturer?: string;
    }): Promise<ApiResponse<{ products: any[]; pagination: any }>> {
        const queryParams = new URLSearchParams();
        queryParams.append('_t', Date.now().toString());
        
        if (params) {
            if (params.page) queryParams.append('page', params.page.toString());
            if (params.limit) queryParams.append('limit', params.limit.toString());
            if (params.search) queryParams.append('search', params.search);
            if (params.category) queryParams.append('category', params.category);
            if (params.stockStatus) queryParams.append('stockStatus', params.stockStatus);
            if (params.manufacturer) queryParams.append('manufacturer', params.manufacturer);
        }
        
        return this.request<{ products: any[]; pagination: any }>(`/api/products?${queryParams.toString()}`);
    }

    static async getProductFilterOptions(): Promise<ApiResponse<{ categories: string[]; manufacturers: string[] }>> {
        return this.request<{ categories: string[]; manufacturers: string[] }>('/api/products/filter-options');
    }

    static async getProduct(productId: string): Promise<ApiResponse<{ product: any }>> {
        return this.request<{ product: any }>(`/api/products/${productId}`);
    }

    static async createProduct(productData: any): Promise<ApiResponse<{ product: any }>> {
        return this.request<{ product: any }>('/api/products', {
            method: 'POST',
            body: JSON.stringify(productData)
        });
    }

    static async updateProduct(productId: string, productData: any): Promise<ApiResponse<{ product: any }>> {
        return this.request<{ product: any }>(`/api/products/${productId}`, {
            method: 'PUT',
            body: JSON.stringify(productData)
        });
    }

    static async deleteProduct(productId: string): Promise<ApiResponse<any>> {
        return this.request(`/api/products/${productId}`, { method: 'DELETE' });
    }

    static async bulkDeleteProducts(productIds: string[]): Promise<ApiResponse<any>> {
        return this.request('/api/products/bulk-delete', {
            method: 'DELETE',
            body: JSON.stringify({ productIds })
        });
    }

    static async bulkImportProducts(products: any[]): Promise<ApiResponse<any>> {
        return this.request('/api/products/bulk-import', {
            method: 'POST',
            body: JSON.stringify({ products })
        });
    }

    static async matchPricesFromOrders(productNames: string[]): Promise<ApiResponse<{ priceMap: any }>> {
        const queryParams = new URLSearchParams();
        productNames.forEach(name => queryParams.append('productNames', name));
        return this.request<{ priceMap: any }>(`/api/products/match-prices?${queryParams.toString()}`);
    }

    static async addProductTranslation(productId: string, country: string, translations: any, prices?: any): Promise<ApiResponse<{ product: any }>> {
        return this.request<{ product: any }>(`/api/products/${productId}/translate`, {
            method: 'POST',
            body: JSON.stringify({ country, translations, prices })
        });
    }

    // Product Opportunities API
    static async getOpportunities(productId?: string): Promise<ApiResponse<{ opportunities: any[] }>> {
        const endpoint = productId 
            ? `/api/products/opportunities/${productId}`
            : '/api/products/opportunities';
        return this.request<{ opportunities: any[] }>(endpoint);
    }

    static async createOpportunity(opportunityData: any): Promise<ApiResponse<{ opportunity: any }>> {
        return this.request<{ opportunity: any }>('/api/products/opportunities', {
            method: 'POST',
            body: JSON.stringify(opportunityData)
        });
    }

    static async updateOpportunity(opportunityId: string, opportunityData: any): Promise<ApiResponse<{ opportunity: any }>> {
        return this.request<{ opportunity: any }>(`/api/products/opportunities/${opportunityId}`, {
            method: 'PUT',
            body: JSON.stringify(opportunityData)
        });
    }

    static async deleteOpportunity(opportunityId: string): Promise<ApiResponse<any>> {
        return this.request(`/api/products/opportunities/${opportunityId}`, {
            method: 'DELETE'
        });
    }

    static async generateVariants(opportunityId: string): Promise<ApiResponse<{ count: number; message: string }>> {
        return this.request<{ count: number; message: string }>(`/api/products/opportunities/${opportunityId}/generate`, {
            method: 'POST'
        });
    }

    // Product Variants API
    static async getVariants(params?: {
        page?: number;
        limit?: number;
        baseProductId?: string;
        enabled?: boolean;
    }): Promise<ApiResponse<{ variants: any[]; pagination: any }>> {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        if (params?.baseProductId) queryParams.append('baseProductId', params.baseProductId);
        if (params?.enabled !== undefined) queryParams.append('enabled', params.enabled.toString());
        
        const queryString = queryParams.toString();
        const endpoint = `/api/products/variants${queryString ? `?${queryString}` : ''}`;
        return this.request<{ variants: any[]; pagination: any }>(endpoint);
    }

    static async getVariant(variantId: string): Promise<ApiResponse<{ variant: any }>> {
        return this.request<{ variant: any }>(`/api/products/variants/${variantId}`);
    }

    static async deleteVariant(variantId: string): Promise<ApiResponse<any>> {
        return this.request(`/api/products/variants/${variantId}`, { method: 'DELETE' });
    }

    static async updateVariant(variantId: string, data: any): Promise<ApiResponse<{ variant: any }>> {
        return this.request(`/api/products/variants/${variantId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    static async createVariant(data: any): Promise<ApiResponse<{ variant: any }>> {
        return this.request('/api/products/variants', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }
}

// Export a default instance for easier usage
export const api = ApiService;
